export type OrganizationLevelId = 1 | 2 | 3 | 4 | 5;
export const OrganizationLevel = {
  IglesiaRegional: 1 as OrganizationLevelId,
  AreaApostol: 2 as OrganizationLevelId,
  Distrito: 3 as OrganizationLevelId,
  GrupoComunidad: 4 as OrganizationLevelId,
  Comunidad: 5 as OrganizationLevelId,
} as const;

export interface OrganizationUnit {
  id: string;
  name: string;
  shortName: string;
  costCentre?: string;
  description?: string;
  parentUnitId?: string;
  regionalChurchId?: string;
  regionalChurchName?: string;
  areaId?: string;
  areaName?: string;
  districtId?: string;
  districtName?: string;
  organizationLevelId: OrganizationLevelId;
  levelName: string;
  leaderId?: string;
  leader?: string;
  leaderMinistryName?: string;
  leaderAppointmentName?: string;
  leaderPhone?: string;
  photo?: string;
  children?: OrganizationUnit[];
  detail?: OrganizationUnitDetail;
}

export interface OrganizationUnitDetail {
  organizationUnitId: string;
  addressLine?: string;
  city?: string;
  department?: string;
  province?: string;
  countryId?: number;
  countryName?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  additionalInfo?: string;
}

export interface PersonMinistryDto {
  personId: string;
  firstName: string;
  lastName: string;
  ministryId: number;
  ministryName: string;
  status: string;
  phone?: string;
  email?: string;
  unitId?: string;
  unitName?: string;
}

export interface MinistryPersonGroupDto {
  ministryId: number;
  ministryName: string;
  sortOrder: number;
  people: PersonMinistryDto[];
}

export type TransferRequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';

export interface MinistryTransferRequest {
  id: string;
  personMinistryId: string;
  personId: string;
  personFirstName: string;
  personLastName: string;
  ministryId: number;
  ministryName: string;
  fromOrganizationUnitId: string;
  fromOrganizationUnitName: string;
  toOrganizationUnitId: string;
  toOrganizationUnitName: string;
  requestedDate: string;
  status: TransferRequestStatus;
  resolvedDate?: string | null;
  resolvedByUserId?: string | null;
  resolutionNotes?: string | null;
}

export interface Ministries {
  id: string;
  name: string;
  role: string;
  ministry?: 'Apóstol' | 'Pastor' | 'Diácono' | 'Siervo';
  phone?: string;
  email?: string;
  isLeader?: boolean;
}

export interface MinistryBearer {
  personId: string;
  firstName: string;
  lastName: string;
  ministryId: number;
  ministryName: string;
  status: string;
}

export interface MinistryGroup {
  ministry: 'Apóstol' | 'Pastor' | 'Diácono' | 'Siervo';
  ministries: Ministries[];
}

export interface Member {
  id: string;
  photo?: string;
  firstName: string;
  lastName: string;
}

export interface MemberPreview {
  items: Member[];
  totalCount: number;
}

export interface MemberLastAttendance {
  personId: string;
  firstName: string;
  lastName: string;
  communityName?: string;
  lastAttendanceAt?: Date;
  divineServiceId?: string;
  divineServiceTypeName?: string;
  hostUnitName?: string;
}

export interface MemberRow {
  id: string;
  photo?: string;
  firstName: string;
  lastName: string;
  status?: import('@core/api/generated/api-client').MembershipStatus;
  isHomeAttended: boolean;
  phone?: string;
  birthDate?: Date;
  age?: number;
  ageStatus?: import('@core/api/generated/api-client').PersonAgeStatus;
  ministryName?: string;
  ministrySortOrder?: number;
  ministryStatus?: import('@core/api/generated/api-client').MinistryStatus;
  activeMinistryUnitIds?: string[];
  communityName?: string;
  communityUnitId?: string;
  responsiblePriestId?: string;
  responsiblePriestName?: string;
  activeAssignments?: { assignmentName: string; unitId: string; unitName: string }[];
  hasLinkedUser: boolean;
}

export interface AttendanceKPI {
  label: string;
  value: number | string;
  unit?: string;
  trend?: 'up' | 'down' | 'stable';
}

export interface DivineServiceSummary {
  id: string;
  date: Date;
  unitName?: string;
  adults: number;
  minors: number;
  total: number;
}

/** Represents a divine-service schedule for a community unit. */
export interface UnitSchedule {
  id: number;
  /** 0 = Domingo, 1 = Lunes, ..., 6 = Sábado */
  dayOfWeek: number;
  /** Format: "HH:mm" */
  startTime: string;
  notes?: string;
}

export type UnitEventTypeId = number;

/** Represents an operational event for a community unit. */
export interface UnitEvent {
  id: number;
  eventTypeId: UnitEventTypeId;
  eventTypeName: string;
  /** Format: "YYYY-MM-DD" */
  eventDate: string;
  notes?: string;
}

export interface ParentChangeHistory {
  id: number;
  organizationUnitId: string;
  fromParentUnitId: string | null;
  fromParentUnitName: string | null;
  toParentUnitId: string | null;
  toParentUnitName: string | null;
  /** Format: "YYYY-MM-DD" */
  effectiveDate: string;
  reason: string | null;
  createdAtUtc: string;
  createdByUserName: string | null;
}

export interface ApostolicAreaDto {
  id: string;
  code: string;
  name: string;
}

export interface DistrictDto {
  id: string;
  code: string;
  name: string;
  apostolicAreaId: string;
}

export interface CommunityGroupDto {
  id: string;
  code: string;
  name: string;
  districtId: string;
}
